import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, IconButton, TextField, Tooltip } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdAssignmentAdd, MdFilterList, MdFilterListOff } from "react-icons/md";
import apiClient from "../services/apiClient";
import SearchIcon from "@mui/icons-material/Search";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";

import { cleanIsUpdate, setModal } from "../features/modal/ModalSlice";
import ConfirmationDialog from "../components/Dialog/ConfirmationDialog";
import toast from "react-hot-toast";

const TeamMembers = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState({
    assignment: false,
    delete: false,
    fetch: false,
  });
  const [showFilter, setShowFilter] = useState(true);
  const [filters, setFilters] = useState({ search: "", name: "", number: "" });

  const userInfo = useSelector((state) => state.userInfo.user);
  const modal = useSelector((state) => state.modal.modal);
  const dispatch = useDispatch();

  const dialogRef = useRef();

  useEffect(() => {
    fetchUsersByCompany();
  }, []);

  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "TEAMS") handleUpdate(modal?.isUpdated?.data);
  }, [modal?.isUpdated]);

  const fetchUsersByCompany = async () => {
    setLoading({
      ...loading,
      fetch: true,
    });
    try {
      const response = await apiClient.get(`/UsersByCompany/${userInfo?.company_id}`);

      setTeam(response?.data?.users || []);
    } catch (error) {
      console.error("Error fetching users by company:", error);
    } finally {
      setLoading({
        ...loading,
        fetch: false,
      });
    }
  };

  const handleUpdate = async (data) => {
    fetchUsersByCompany();
    dispatch(cleanIsUpdate());
  };

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ search: "", name: "", number: "" });
  };

  const nameOptions = useMemo(
    () => [...new Set(team.map((member) => member?.name).filter(Boolean))],
    [team]
  );

  const numberOptions = useMemo(
    () => [
      ...new Set(
        team.flatMap((member) => member?.extensions?.map((ext) => ext?.data?.username) || [])
      ),
    ].filter(Boolean),
    [team]
  );

  const filteredTeam = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return team.filter((member) => {
      if (
        search &&
        !member?.name?.toLowerCase().includes(search) &&
        !member?.email_id?.toLowerCase().includes(search)
      ) {
        return false;
      }
      if (filters.name && member?.name !== filters.name) return false;
      if (
        filters.number &&
        !member?.extensions?.some((ext) => ext?.data?.username === filters.number)
      ) {
        return false;
      }
      return true;
    });
  }, [team, filters]);

  const handleAddMember = () => {
    dispatch(setModal({ type: "ADD_MEMBER" }));
  };

  const handleConfigureNumberPopUp = (data = null) => {
    dispatch(setModal({ type: "CONFIGURE_NUMBER", data }));
  };

  // Soft delete — removes from team, data preserved, extension unassigned
  const handleRemoveMember = async (user) => {
    const isConfirmed = await dialogRef.current.show(
      "Remove Member",
      `Remove ${user?.name || "this member"} from the team? Their data and call history will be preserved.`
    );
    if (!isConfirmed) return;

    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      await apiClient.put("/removeMember", {
        user_id: user._id,
        company_id: userInfo?.company_id,
      });
      toast.success("Member removed from team successfully");
      setTeam((prev) => prev.filter((m) => m._id !== user._id));
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  // Hard delete — permanently deletes user and all their assigned phone details
  const handleDeleteMember = async (user) => {
    const isConfirmed = await dialogRef.current.show(
      "Delete Member Permanently",
      `Permanently delete ${user?.name || "this member"}? This cannot be undone and will remove all their data.`
    );
    if (!isConfirmed) return;

    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      await apiClient.delete("/deleteMember", {
        user_id: user._id,
        company_id: userInfo?.company_id,
      });
      toast.success("Member deleted permanently");
      setTeam((prev) => prev.filter((m) => m._id !== user._id));
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Failed to delete member. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleRemoveExtension = async (user) => {
    const isConfirmed = await dialogRef.current.show(
      "Confirm",
      "Are you sure you want to un assign this extension for this user?"
    );
    if (!isConfirmed) {
      return;
    }

    setLoading({
      ...loading,
      assignment: true,
    });

    try {
      const payload = {
        user_id: user?._id,
        assigned_phone_status: "not-assigned",
      };
      await apiClient.put(`/assignedPhoneDetails-update/${user?.extensions[0]?._id}`, payload);

      toast.success("Extension Unassigned!");

      fetchUsersByCompany();
    } catch (error) {
      console.error("Error assigning extension:", error);
      toast.error("Oops! An error occurred Please try again!");
    } finally {
      setLoading({
        ...loading,
        assignment: false,
      });
    }
  };

  //MRT configuration
  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Name" },
      { accessorKey: "email_id", header: "Email" },
      {
        accessorKey: "extensions",
        header: "Extension",
        Cell: ({ cell }) => {
          const data = cell.getValue();
          if (!data) return "";
          const value = data.map((extension) => extension?.data?.username).join(", ");
          return value;
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredTeam,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton
          title="Assign extension"
          onClick={() => handleConfigureNumberPopUp(row?.original)}
          disabled={row?.original?.extensions.length}
        >
          <MdAssignmentAdd />
        </IconButton>
        <IconButton
          title="un-assign extension"
          onClick={() => handleRemoveExtension(row?.original)}
          disabled={!row?.original?.extensions.length}
        >
          <PhonelinkEraseIcon />
        </IconButton>
        <IconButton
          title="Remove from team (data preserved)"
          onClick={() => handleRemoveMember(row?.original)}
        >
          <GroupRemoveIcon />
        </IconButton>
        <IconButton
          title="Delete permanently"
          onClick={() => handleDeleteMember(row?.original)}
          sx={{ color: "error.main" }}
        >
          <DeleteForeverIcon />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      {(loading?.fetch || loading?.assignment || loading?.delete) && (
        <PageLoader
          text={loading?.fetch ? "Loading Teams..." : loading?.assignment ? "Unassigning Extension..." : "Processing..."}
        />
      )}

      <ConfirmationDialog ref={dialogRef} />

      <div className="pageTitle1 p-5">
        <h3>Team Members</h3>
        <p>{filteredTeam?.length} Available</p>
      </div>

      <Box sx={{ padding: 2 }}>
        <div className="d-flex justify-content-between">
          <div className="w-50">
            <CustomInputBox
              icon={SearchIcon}
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search Team Members"
              type="text"
              backgroundColor={"#FFFFFF"}
              borderRadius="8px"
            />
          </div>

          <div className="d-flex justify-content-end gap-3 mb-4">
            <CustomButton
              text={(showFilter ? "Hide" : "Show") + " Filter"}
              color="secondary"
              iconPosition="left"
              icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
              onClick={handleShowFilter}
            />

            <CustomButton text={"Add Member"} onClick={handleAddMember} />
          </div>
        </div>

        {showFilter && (
          <div className="d-flex justify-content-start gap-3 mb-4">
            <Autocomplete
              disablePortal
              options={nameOptions}
              value={filters.name || null}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, name: newValue || "" }));
              }}
              sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
              size="small"
              renderInput={(params) => <TextField {...params} label="Name" />}
            />

            <Autocomplete
              disablePortal
              options={numberOptions}
              value={filters.number || null}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, number: newValue || "" }));
              }}
              sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
              size="small"
              renderInput={(params) => <TextField {...params} label="Number" />}
            />

            <CustomButton
              text={"Reset"}
              variant="text"
              color="inherit"
              onClick={handleResetFilters}
            />
          </div>
        )}

        <MaterialReactTable table={table} />
      </Box>
    </>
  );
};

export default TeamMembers;
